/**
 * Debug Logger Utility
 *
 * Provides conditional debug logging that can be toggled via localStorage.
 * Debug mode is ON by default.
 *
 * Usage:
 * - Disable debug mode: localStorage.setItem('DEBUG_MODE', 'false')
 * - Enable debug mode: localStorage.removeItem('DEBUG_MODE')
 * - Check status: localStorage.getItem('DEBUG_MODE')
 *
 * In code:
 * import { debugLog } from '@/utils/debugLogger'
 * debugLog('My debug message', { data: 'value' })
 */

const DEBUG_MODE_KEY = 'DEBUG_MODE'

/**
 * Check if debug mode is enabled
 * Default: ON (returns true unless explicitly disabled)
 */
export const isDebugMode = (): boolean => {
  if (typeof window === 'undefined') return false
  // Debug mode is ON by default, unless explicitly set to 'false'
  const debugSetting = localStorage.getItem(DEBUG_MODE_KEY)
  return debugSetting !== 'false'
}

/**
 * Enable debug mode (default state, removes any disable flag)
 */
export const enableDebugMode = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(DEBUG_MODE_KEY) // Remove disable flag
    console.log('✅ Debug mode ENABLED (default). Reload the page to see debug logs.')
  }
}

/**
 * Disable debug mode
 */
export const disableDebugMode = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(DEBUG_MODE_KEY, 'false')
    console.log('✅ Debug mode DISABLED. Reload the page to hide debug logs.')
  }
}

/**
 * Log a debug message (only if debug mode is enabled)
 */
export const debugLog = (...args: any[]): void => {
  if (isDebugMode()) {
    console.log('🚀 [DEBUG]', ...args)
  }
}

/**
 * Log a debug error (only if debug mode is enabled)
 */
export const debugError = (...args: any[]): void => {
  if (isDebugMode()) {
    console.error('❌ [DEBUG]', ...args)
  }
}

/**
 * Log a debug warning (only if debug mode is enabled)
 */
export const debugWarn = (...args: any[]): void => {
  if (isDebugMode()) {
    console.warn('⚠️ [DEBUG]', ...args)
  }
}

// Expose debug controls to window for easy access in browser console
if (typeof window !== 'undefined') {
  (window as any).debugMode = {
    enable: enableDebugMode,
    disable: disableDebugMode,
    isEnabled: isDebugMode,
    help: () => {
      console.log(`
🔧 Debug Mode Controls:

  Debug mode is ON by default.

  Disable: window.debugMode.disable() or  localStorage.setItem('DEBUG_MODE', 'false')
  Enable:  window.debugMode.enable()  or  localStorage.removeItem('DEBUG_MODE')
  Status:  window.debugMode.isEnabled()

  After enabling/disabling, reload the page to see changes.
      `)
    }
  }

  // Log debug mode status on page load
  if (isDebugMode()) {
    console.log('%c🚀 DEBUG MODE ENABLED (default)', 'background: #00ff00; color: #000; font-size: 16px; font-weight: bold; padding: 4px 8px;')
    console.log('Debug logs will appear with 🚀 [DEBUG] prefix')
    console.log('To disable: window.debugMode.disable()')
  } else {
    console.log('%cℹ️ Debug mode is OFF', 'background: #ccc; color: #000; font-size: 12px; padding: 2px 4px;')
    console.log('To enable: window.debugMode.enable()')
  }
}

